/**
 * Portal Course Group Material Assignment API Client
 *
 * Handles operations for material assignments to course groups with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to course group material endpoint.
 */
const materialApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/course-group-material`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * Make an authenticated API request to course group material access endpoint.
 */
const accessApiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/course-group-material-access`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

// ==================== Material Assignment API ====================

/**
 * List materials assigned to a course group with access rules.
 * Returns both materials and answers separately, with accessibility flags.
 * For teachers, materials are filtered by assigned subjects.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const listMaterials = async (courseGroupId, viewAs = null) => {
  const data = { courseGroupId };
  if (viewAs) data.viewAs = viewAs;
  const result = await materialApiRequest('list', data);
  return {
    materials: result.materials || [],
    answers: result.answers || [],
    currentWeek: result.currentWeek || 0,
    courseGroup: result.courseGroup || {},
    teacherSubjectIds: result.teacherSubjectIds || null,
    message: result.message || null
  };
};

/**
 * Assign a material to a course group with optional week and schedule assignment.
 * Answers are now fetched in real-time from MaterialAnswerPortal when listing.
 *
 * @param {string} courseGroupId - Course group ID
 * @param {string} materialId - Material ID
 * @param {number} assignedWeek - Optional week number to assign to (for regular terms)
 * @param {number} assignedScheduleIndex - Optional schedule index to assign to (for regular terms)
 * @param {number} assignedSlotIndex - Optional slot index to assign to (for special terms)
 * @param {number} assignedTerm - Optional term override; defaults server-side to the
 *   course group's current term if omitted
 */
export const assignMaterial = async (
  courseGroupId, materialId, assignedWeek = null, assignedScheduleIndex = null,
  assignedSlotIndex = null, assignedTerm = null
) => {
  const data = { courseGroupId, materialId };
  if (assignedWeek !== null) {
    data.assignedWeek = assignedWeek;
  }
  if (assignedScheduleIndex !== null) {
    data.assignedScheduleIndex = assignedScheduleIndex;
  }
  if (assignedSlotIndex !== null) {
    data.assignedSlotIndex = assignedSlotIndex;
  }
  if (assignedTerm !== null) {
    data.assignedTerm = assignedTerm;
  }
  const result = await materialApiRequest('assign', data);
  return result.assignments || [];
};

/**
 * Remove a material from a course group.
 *
 * @param {string} assignmentId - Assignment ID
 */
export const unassignMaterial = async (assignmentId) => {
  const result = await materialApiRequest('unassign', { assignmentId });
  return result;
};

/**
 * Remove ALL material assignments from a course group in one call.
 *
 * @param {string} courseGroupId - Course group ID
 * @returns {Object} { success, deletedCount, message }
 */
export const batchUnassignMaterials = async (courseGroupId) => {
  const result = await materialApiRequest('batchUnassign', { courseGroupId });
  return {
    success: result.success || false,
    deletedCount: result.deletedCount || 0,
    message: result.message || ''
  };
};

/**
 * End-of-term cleanup for a course group that will be reused for the next term:
 * keeps whatever is currently assigned to Week 9 and Week 10 (re-tagged with the
 * term that is ending, and pinned to the very start of the teacher +/-1 window
 * so it only stays visible through the new term's first week), and removes
 * every other material assignment. Regular-term course groups only.
 *
 * @param {string} courseGroupId - Course group ID
 * @returns {Object} { success, retainedCount, removedCount, retainedTerm, message }
 */
export const termEndRemove = async (courseGroupId) => {
  const result = await materialApiRequest('termEndRemove', { courseGroupId });
  return {
    success: result.success || false,
    retainedCount: result.retainedCount || 0,
    removedCount: result.removedCount || 0,
    retainedTerm: result.retainedTerm,
    message: result.message || ''
  };
};

/**
 * Batch assign multiple materials to a course group.
 * Answers are now fetched in real-time from MaterialAnswerPortal when listing.
 *
 * @param {string} courseGroupId - Course group ID
 * @param {string[]} materialIds - Array of material IDs
 * @param {boolean} autoMatch - If true, auto-assign week and schedule based on material metadata
 */
export const batchAssignMaterials = async (courseGroupId, materialIds, autoMatch = false) => {
  const result = await materialApiRequest('batchAssign', { courseGroupId, materialIds, autoMatch });
  return {
    assignedCount: result.assignedCount || 0,
    skippedCount: result.skippedCount || 0,
    autoMatchedCount: result.autoMatchedCount || 0,
    unmatchedDetails: result.unmatchedDetails || [],
    unmatchedReasonCounts: result.unmatchedReasonCounts || {},
    hasSchedulesWithoutSubject: result.hasSchedulesWithoutSubject || false
  };
};

/**
 * Re-run auto-match on existing unmatched assignments.
 * Only updates assignments missing week/schedule/slot.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const reAutoMatch = async (courseGroupId) => {
  const result = await materialApiRequest('reAutoMatch', { courseGroupId });
  return {
    matchedCount: result.matchedCount || 0,
    skippedCount: result.skippedCount || 0,
    unmatchedDetails: result.unmatchedDetails || [],
    unmatchedReasonCounts: result.unmatchedReasonCounts || {}
  };
};

/**
 * Clean up legacy answer assignments from CourseGroupMaterialPortal.
 * Only system admins can run this operation.
 *
 * @returns {Object} Cleanup statistics { deletedCount, skippedCount, errorCount }
 */
export const cleanupAnswerAssignments = async () => {
  const result = await materialApiRequest('cleanupAnswers', {});
  return {
    deletedCount: result.deletedCount || 0,
    skippedCount: result.skippedCount || 0,
    errorCount: result.errorCount || 0,
    message: result.message || ''
  };
};

/**
 * List all available materials for assignment.
 * If courseGroupId is provided, only materials with subjects in that course group are returned.
 *
 * @param {Object} filters - Optional filters { courseGroupId, subjectId, year, term, isAnswer }
 */
export const listAvailableMaterials = async (filters = {}) => {
  const result = await materialApiRequest('listAvailable', filters);
  return result.materials || [];
};

/**
 * Calculate all weeks for a course group based on date range and holiday weeks.
 *
 * @param {string} courseGroupId - Course group ID
 * @returns {Object} { weeks: [], totalWeeks: number, totalTeachingWeeks: number, currentWeek: number }
 */
export const calculateWeeks = async (courseGroupId) => {
  const result = await materialApiRequest('calculateWeeks', { courseGroupId });
  return {
    weeks: result.weeks || [],
    totalWeeks: result.totalWeeks || 0,
    totalTeachingWeeks: result.totalTeachingWeeks || 0,
    currentWeek: result.currentWeek || 0,
    rawWeek: result.rawWeek || 0,
    isHoliday: result.isHoliday || false
  };
};

/**
 * Shift material week assignments after a course group's holidayWeeks changed.
 * Preserves each material's position in the teaching-week sequence (skips over
 * unrelated holiday weeks); materials that would overflow past the last
 * calculated week are left unchanged. No-op for special-term course groups.
 *
 * @param {string} courseGroupId - Course group ID
 * @param {number[]} oldHolidayWeeks - Holiday weeks before the edit
 * @param {number[]} newHolidayWeeks - Holiday weeks after the edit
 * @returns {Object} { shiftedCount: number, shifted: Array<{assignmentId, materialId, oldWeek, newWeek}> }
 */
export const shiftMaterialWeeks = async (courseGroupId, oldHolidayWeeks, newHolidayWeeks) => {
  const result = await materialApiRequest('shiftMaterialWeeks', {
    courseGroupId,
    oldHolidayWeeks,
    newHolidayWeeks
  });
  return {
    success: result.success || false,
    shiftedCount: result.shiftedCount || 0,
    shifted: result.shifted || [],
    overflowCount: result.overflowCount || 0,
    overflow: result.overflow || []
  };
};

/**
 * Update the assigned week for a material in a course group.
 *
 * @param {string} assignmentId - Assignment ID
 * @param {number|null} assignedWeek - Week number to assign to, or null to unassign
 */
export const updateMaterialWeek = async (assignmentId, assignedWeek) => {
  const result = await materialApiRequest('updateMaterialWeek', { assignmentId, assignedWeek });
  return {
    success: result.success || false,
    assignedWeek: result.assignedWeek
  };
};

/**
 * Update the assigned week and schedule for a material in a course group.
 *
 * @param {string} assignmentId - Assignment ID
 * @param {number|null} assignedWeek - Week number to assign to, or null to unassign
 * @param {number|null} assignedScheduleIndex - Schedule index to assign to, or null to unassign
 * @param {number|undefined} assignedTerm - Optional term override; omit to leave unchanged,
 *   pass null to clear it, or a number to set it (manual "adjust the term" override)
 */
export const updateMaterialAssignment = async (
  assignmentId, assignedWeek, assignedScheduleIndex = null, assignedTerm = undefined
) => {
  const data = { assignmentId, assignedWeek, assignedScheduleIndex };
  if (assignedTerm !== undefined) {
    data.assignedTerm = assignedTerm;
  }
  const result = await materialApiRequest('updateMaterialWeek', data);
  return {
    success: result.success || false,
    assignedWeek: result.assignedWeek,
    assignedScheduleIndex: result.assignedScheduleIndex
  };
};

/**
 * Update the assigned slot for a material in a special term course group.
 *
 * @param {string} assignmentId - Assignment ID
 * @param {number|null} assignedSlotIndex - Slot index to assign to, or null to unassign
 */
export const updateMaterialSlot = async (assignmentId, assignedSlotIndex) => {
  const result = await materialApiRequest('updateMaterialWeek', { assignmentId, assignedSlotIndex });
  return {
    success: result.success || false,
    assignedSlotIndex: result.assignedSlotIndex
  };
};

// ==================== Material Access API ====================

/**
 * Check if user can view/print a material in course group context.
 *
 * @param {string} courseGroupId - Course group ID
 * @param {string} materialId - Material ID
 * @param {string} operationType - 'VIEW' or 'PRINT'
 */
export const checkAccess = async (courseGroupId, materialId, operationType = 'VIEW') => {
  const result = await accessApiRequest('checkAccess', { courseGroupId, materialId, operationType });
  return {
    canAccess: result.canAccess || false,
    remaining: result.remaining,
    limit: result.limit,
    used: result.used || 0
  };
};

/**
 * Record access and get presigned URL for material.
 *
 * @param {string} courseGroupId - Course group ID
 * @param {string} materialId - Material ID
 * @param {string} operationType - 'VIEW' or 'PRINT'
 */
export const recordAccess = async (courseGroupId, materialId, operationType = 'VIEW') => {
  const result = await accessApiRequest('recordAccess', { courseGroupId, materialId, operationType });
  return {
    url: result.url,
    remaining: result.remaining,
    limit: result.limit
  };
};

/**
 * Get access statistics for a course group.
 * Admin roles only.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const getAccessStats = async (courseGroupId) => {
  const result = await accessApiRequest('getAccessStats', { courseGroupId });
  return {
    stats: result.stats || [],
    totalRecords: result.totalRecords || 0
  };
};

const courseGroupMaterialApi = {
  listMaterials,
  assignMaterial,
  unassignMaterial,
  batchUnassignMaterials,
  termEndRemove,
  batchAssignMaterials,
  reAutoMatch,
  listAvailableMaterials,
  cleanupAnswerAssignments,
  calculateWeeks,
  updateMaterialWeek,
  updateMaterialAssignment,
  updateMaterialSlot,
  checkAccess,
  recordAccess,
  getAccessStats
};

export default courseGroupMaterialApi;
